// Counting a stock take (UC-INVENTORY-008) and reviewing it (UC-INVENTORY-009, -010). The count screen keeps its scan box focused —
// a keyboard-wedge scanner types a code and Enter, a tablet's camera reads one into the same box — and answers each scan in words
// (counted, already counted by whom, found and posting nothing, unknown); a product picked and a typed quantity count a shelf at
// once, a serialised product's unit without a serial as a found unit. A scan or an Add whose answer was lost is kept, and sent again
// only once the counter answers that it is the same box (UC-INVENTORY-008 A11 step 3). Entries from several devices add up, each
// saying who counted it on which device; a wrong one is removed, which stays on record, and the screen refreshes every few seconds so
// another device's entries appear. The review lists each line's expected, counted, variance and value at the branch's weighted
// average, the missing and found units, what moved during the count and the units at a lab or in transit apart; Finalise asks
// for a reason when something differs and the Control PIN (`StockTakeFinalise`), and a variance that moved since it was read is
// shown again. An opening count's review takes each line's unit cost — the price list's where one carries it, else the product's
// supplier price's (change 9) — and the cut-over
// date, and posts the opening stock under the same PIN — one too large for one stock write posts in parts, and the count then
// reads "Posting… 340 of 1 000 lines posted" until it is finalised (UC-INVENTORY-010 A4), its review showing each line's cost as
// the posting holds it. A posting whose job stopped reads "Posting stopped at 340 of 1 000 lines: …" with why, and offers Post again
// (A7) and End posting… (A8). Abandon asks for a reason and posts nothing.
import { IDEMPOTENCY_RETENTION_HOURS, cents, type Cents, type FieldError, type ProductSummary, type StockCountEntryView, type StockTakeLineView, type StockTakeSummary, type StockTakeView, type VarianceView } from "@visionx/contracts";
import { sumCents, timesQuantity } from "@visionx/money";
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { stock, stockTakes, type CountBody } from "../api/endpoints.inventory";
import { carriedOut, describeError, isApiError, isNetworkError } from "../api/problem";
import { BURST_GAP_MS, inBurst, isTypingTarget, scanCollector } from "../shell/key-burst";
import { formatCount, formatMoney, formatTime, localDate } from "../lib/format";
import { centsFromText, textFromCents } from "../lib/money-input";
import { isOnline } from "../lib/online";
import { can, useSessionView } from "../session/store";
import { useOneStore } from "../session/one-store";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Empty, ErrorAlert, Field, Loading, LockMark, Modal, Panel, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { ProductPicker } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { CameraScan, StockTakeStatusChip, dayRange, personWithId, readDeviceName, scopeWords, shortName, signed, useStockWrites, writeDeviceName } from "./StockShared";

/**
 * Words on the screen, and — where they say a lost answer's code or entry, sent again, is counted once — the first send of its key and
 * what they say instead once it is past the Worker's hold (`pastHold`), as the notice above the scan box does: a re-send then is a new
 * count (UC-INVENTORY-008 A11 step 2; the verification of round 8's fixes, 26 Oct 2026, V8I-2: with the screen left open across the bound
 * only the notice changed, while the latest answer and the Quantity field still said it is counted once; TC-INVENTORY-246).
 */
interface Said<T> { text: T; once?: { firstSentAt: number; past: T } | undefined }
type Answer = Said<ReactNode> & { tone: "ok" | "info" | "warn" | "crit" };

/**
 * What lost answers leave on this device (UC-INVENTORY-008 A11 step 2), for the person signed in and the count: each scan whose answer
 * did not arrive — its key and what it sent, by its code — and each Add whose answer did not arrive — its key and what it sent, by the
 * entry it counts: its product, quantity, and a found unit's supplier serial and old sticker, never the device's name (V7IR-1).
 * Each is kept until that same code or entry is sent again and answered, its count left and opened again included: another code's or
 * entry's send, answer or refusal never touches it, and a send refused before it leaves changes nothing. Until the verification of
 * round 6's fixes (25 Oct 2026, L6I-2, M1) the screen kept one key, which the next code sent dropped, so a lost pack with another code
 * sent between it and its re-scan — the queue sends the next at once — was counted twice (TC-INVENTORY-238, -239). One store a query
 * client, as the receipt's (`pendingReceipts`, StockTransfer.tsx), so each test's client starts with none; a reload of the page drops it.
 * Kept by the person and the count, as the Worker binds a key to its person and its path (TC-INVENTORY-243); an Add's kept with the
 * words the screen names it by — "3 × Biofinity Toric · monthly · 6 pack" — while it waits to be sent again (the verification of
 * round 7's fixes, 25 Oct 2026, V7IS-1; TC-INVENTORY-239). Each with the time of its first send, on this device's clock: it is sent
 * again only while the Worker still holds it (`pastHold`; the verification of round 7's fix-ups, 25 Oct 2026, V7IR-4).
 * Sent again only once the counter answers that it is the same box, or the same units (A11 step 3; DVN's answers of 28 Oct 2026,
 * change 12): another box of it, counted under a key of its own, leaves it kept, and if that answer is lost too it is kept beside it —
 * so each is kept by its key, in the order first sent, a code or an entry kept more than once, the oldest sent again first
 * (TC-INVENTORY-254). An Add's entry is `what` it counts; `code` is a scan's.
 */
interface Kept { key: string; body: CountBody; firstSentAt: number }
interface KeptScan extends Kept { code: string }
interface KeptAdd extends Kept { what: string; entry: string }
interface LostAnswers { scans: Map<string, KeptScan>; adds: Map<string, KeptAdd> }
const lostAnswers = new WeakMap<QueryClient, Map<string, LostAnswers>>();
function lostAnswersOf(qc: QueryClient, slot: string): LostAnswers {
  let byCount = lostAnswers.get(qc);
  if (!byCount) { byCount = new Map(); lostAnswers.set(qc, byCount); }
  let lost = byCount.get(slot);
  if (!lost) { lost = { scans: new Map(), adds: new Map() }; byCount.set(slot, lost); }
  return lost;
}

/** The entries behind a line: a serial's own, else the product's without a serial. */
const entriesOf = (l: StockTakeLineView, entries: readonly StockCountEntryView[]) =>
  entries.filter((e) => (l.serialId ? e.serialId === l.serialId : e.productId === l.productId && e.serialId === null));

/** "a, b and c" of nodes. */
const andNodes = (xs: readonly ReactNode[]): ReactNode => xs.map((x, i) => <Fragment key={i}>{i === 0 ? null : i === xs.length - 1 ? " and " : ", "}{x}</Fragment>);

/**
 * How long a lost answer's key is sent again, from its first send, on this device's clock (UC-INVENTORY-008 A11 step 2): the Worker keeps
 * a key's record IDEMPOTENCY_RETENTION_HOURS — 24 — from the first send that reached it, after which a request under it is a new one,
 * counted again; an hour less keeps a margin for a re-send's way there and its wait in the queue, and for this device's clock set back
 * meanwhile — each side measures the time on its own clock, so the two reading apart does not matter (the verification of round 8's
 * fixes, 26 Oct 2026). Past it the code or entry is named as possibly counted, and its next scan or Add is a new count, under a key of
 * its own (the verification of round 7's fix-ups, 25 Oct 2026, V7IR-4: until then a key had no age and outlived a sign-out, so a count
 * left open over a weekend asked on Monday for a re-scan "counted once" that the Worker counted again; TC-INVENTORY-246). A device clock
 * set back while a key is kept stretches it (Proposed, UC-INVENTORY-008's open questions).
 */
const KEPT_KEY_MS = (IDEMPOTENCY_RETENTION_HOURS - 1) * 3_600_000;
/** Whether a kept key is past the Worker's hold, as this device's clock reads it at `now`. */
const pastHold = (k: { firstSentAt: number }, now: number): boolean => now - k.firstSentAt >= KEPT_KEY_MS;
/** A code's or an entry's kept sends the Worker still holds at `now`, the oldest first: what a Same box sends again (A11 steps 2–3). */
const heldOf = <K extends Kept>(kept: Map<string, K>, of: (k: K) => boolean, now: number): K[] => [...kept.values()].filter((k) => of(k) && !pastHold(k, now));
/** Drops a code's or an entry's kept sends past the hold — a new count of it answered, or lost, leaves them (step 2); whether any went. */
function dropPast<K extends Kept>(kept: Map<string, K>, of: (k: K) => boolean, now: number): boolean {
  let dropped = false;
  for (const [key, k] of kept) if (of(k) && pastHold(k, now)) { kept.delete(key); dropped = true; }
  return dropped;
}
/** What words say at `now`: past the hold of the key they promise a re-send of, what they say instead. */
const saidAt = <T,>(x: Said<T>, now: number): T => (x.once && pastHold(x.once, now) ? x.once.past : x.text);
/** A lost scan's answer, or its re-scan's refused offline, once its key is past the hold; and Add's under Quantity (V8I-2). */
const pastScanAnswer = (c: string): ReactNode => <><span className="mono">{c}</span> may have been counted: its answer did not arrive a day ago or more. Check its line below before you scan it again.</>;
const PAST_ADD_FIELD = "The answer did not arrive a day ago or more, so it may have been counted: check its line below before you add it again";

/** A kept line's words after its names: within the Worker's hold its re-send counts once; past it, it may have been counted already. */
function keptWords(n: number, verb: "scan" | "add", past: boolean): string {
  const again = verb === "scan" ? "Scan" : "Add";
  if (!past) return n === 1 ? `its answer did not arrive. ${again} it again — it is counted once.` : `their answers did not arrive. ${again} each again — each is counted once.`;
  return n === 1
    ? `its answer did not arrive a day ago or more, so it may have been counted. Check its line below before you ${verb} it again.`
    : `their answers did not arrive a day ago or more, so each may have been counted. Check each one's line below before you ${verb} it again.`;
}

/**
 * What waits on this device to be sent again (UC-INVENTORY-008 A11 step 2): each code and Add entry whose answer did not arrive, named
 * for as long as its key is kept — its count opened again and any number of answers later included — so the counter knows that the next
 * scan of that code, or the next Add of that entry, is its re-send. Until the verification of round 7's fixes (25 Oct 2026, V7IS-1) only
 * the answers said it — the latest four, none on a count opened again — while the key went on working (TC-INVENTORY-239). Several in one
 * line, in the plural (TC-INVENTORY-247); past the Worker's hold a line of their own, saying each may have been counted and to check its
 * line below — its entries, by whom, on which device and when — before it is sent again (V7IR-4; TC-INVENTORY-246). A code or an entry
 * kept more than once — another box whose answer was lost too (A11 step 3) — is named once with how many: "6009602000023 (2 scans)"
 * (TC-INVENTORY-254). A standing notice, not an answer: it takes no live role, so the latest answer alone is read out.
 */
function KeptToSendAgain({ lost, now }: { lost: LostAnswers; now: number }) {
  type Named = { id: string; name: ReactNode; past: boolean };
  const scans: Named[] = [...lost.scans.values()].map((k) => ({ id: k.code, name: <span className="mono">{k.code}</span>, past: pastHold(k, now) }));
  const adds: Named[] = [...lost.adds.values()].map((k) => ({ id: k.what, name: k.entry, past: pastHold(k, now) }));
  const lines: { label: string; verb: "scan" | "add"; past: boolean; names: ReactNode[]; sends: number }[] = [];
  for (const [label, verb, kept, unit] of [["Not scanned again yet:", "scan", scans, "scans"], ["Not added again yet:", "add", adds, "entries"]] as const) {
    for (const past of [false, true]) {
      const sends = kept.filter((x) => x.past === past);
      if (sends.length === 0) continue;
      // Each code or entry once, in the order first kept, with how many of its sends the line holds where it is more than one.
      const once = new Map<string, { name: ReactNode; n: number }>();
      for (const x of sends) { const g = once.get(x.id); if (g) g.n++; else once.set(x.id, { name: x.name, n: 1 }); }
      lines.push({ label, verb, past, sends: sends.length, names: [...once.values()].map((g) => (g.n === 1 ? g.name : <>{g.name} ({g.n} {unit})</>)) });
    }
  }
  if (lines.length === 0) return null;
  return (
    <div className="alert warn" data-testid="count-kept">
      <Icon name="warning" />
      <div className="grow stack gap-4">
        {lines.map((l) => <div key={`${l.verb}-${l.past}`} data-testid="count-kept-line"><b>{l.label}</b> {andNodes(l.names)} — {keptWords(l.sends, l.verb, l.past)}</div>)}
      </div>
    </div>
  );
}

/** What the count screen asks before a kept code or entry is sent again (UC-INVENTORY-008 A11 step 3), and how it is answered. */
interface Question { kind: "scan" | "add"; name: ReactNode; kept: number; quantity: number }
type Reply = "same" | "another" | "left";
/** A Y or N is taken this long after it is typed, once no key at a scanner's pace has followed it (A11 step 4). */
const ANSWER_AFTER_MS = 2 * BURST_GAP_MS;
/** Enter and Space on the question's buttons do nothing: a click, a tap, Y or N answers it (A11 step 4). */
const noEnter = (e: ReactKeyboardEvent<HTMLButtonElement>) => { if (e.key === "Enter" || e.key === " ") e.preventDefault(); };

/**
 * The question's words (A11 step 3): of a scan, "Is this the same box?"; of an Add, "Are these the same units?" — of a quantity of one
 * "Is this the same unit?" — each saying what its two answers do, and of a code or an entry kept more than once how many answers did not
 * arrive (TC-INVENTORY-250, TC-INVENTORY-251, TC-INVENTORY-254).
 */
function questionWords(q: Question): { head: string; same: string; another: string; text: ReactNode; help: string } {
  const one = q.kept === 1;
  const lostOf = one ? "its answer" : `${q.kept} of its answers`;
  if (q.kind === "scan") {
    return {
      head: "Is this the same box?", same: "Same box", another: "Another box", text: <>{q.name} was scanned before and {lostOf} did not arrive.</>,
      help: `Same box sends ${one ? "it" : "one of them"} again as it was first sent, so it is counted once. Another box is counted as well, and the lost ${one ? "scan stays" : "scans stay"} named above the scan box, to be scanned again.`,
    };
  }
  const entries = `the lost ${one ? "entry stays" : "entries stay"} named above the scan box, to be added again.`;
  const text = <>{q.name} was added before and {lostOf} did not arrive.</>;
  return q.quantity === 1
    ? { head: "Is this the same unit?", same: "Same unit", another: "Another unit", text, help: `Same unit sends it again as it was first sent, so it is counted once. Another unit is counted as well, and ${entries}` }
    : { head: "Are these the same units?", same: "Same units", another: "Other units", text, help: `Same units sends them again as they were first sent, so they are counted once. Other units are counted as well, and ${entries}` };
}

/**
 * Whether a code or an entry whose answer was lost, about to be sent again, is the same box — the same units, for Add — or another
 * (UC-INVENTORY-008 A11 steps 3–4; DVN's answers of 28 Oct 2026 to the Sprint 4 board's question 62, "Screen must ask if same box";
 * change 12). It takes the focus, and neither answer is its default: a click, a tap, or a person's Y or N typed outside a field answers
 * it — never with Ctrl, Alt or ⌘, by a key's repeat or under a dialog. Enter answers nothing, on the question or either button, nor Space
 * there, and a scanner's code and its Enter neither answer it nor close it, a code with a Y or an N in it included: a Y or N is taken
 * only once no key has followed it at a scanner's pace (`key-burst.ts`), so a code that begins with one is a scan (TC-INVENTORY-252).
 * Scans meanwhile are taken as when the focus is off the box (A10) and wait behind it (step 5). While the count posts it is `held` (step 9).
 */
function SameOrAnother({ q, held, onReply }: { q: Question; held: string | null; onReply: (r: "same" | "another") => void }) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const reply = useRef(onReply);
  reply.current = held === null ? onReply : () => {};
  useEffect(() => { ref.current?.focus(); }, []);
  useEffect(() => {
    let wait: ReturnType<typeof setTimeout> | null = null;
    const onKey = (e: KeyboardEvent) => {
      // A key at a scanner's pace after a Y or N makes it the first key of a code: no answer.
      if (wait !== null && inBurst(e)) { clearTimeout(wait); wait = null; }
      if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey || e.repeat || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const k = e.key.toLowerCase();
      if ((k !== "y" && k !== "n") || inBurst(e)) return;
      e.preventDefault();
      if (wait !== null) clearTimeout(wait);
      wait = setTimeout(() => { wait = null; reply.current(k === "y" ? "same" : "another"); }, ANSWER_AFTER_MS);
    };
    window.addEventListener("keydown", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); if (wait !== null) clearTimeout(wait); };
  }, []);
  const words = questionWords(q);
  return (
    <div ref={ref} className="alert warn count-ask" role="alertdialog" aria-labelledby={`${id}-q`} aria-describedby={`${id}-t ${id}-h`} tabIndex={-1}>
      <Icon name="warning" />
      <div className="grow stack gap-4">
        <div><b id={`${id}-q`}>{words.head}</b> <span id={`${id}-t`} data-testid="count-ask-text">{words.text}</span></div>
        <div id={`${id}-h`} className="small" data-testid="count-ask-help">{held ?? words.help}</div>
      </div>
      <div className="act">
        <button className="btn lg" type="button" disabled={held !== null} aria-keyshortcuts="Y" onKeyDown={noEnter} onKeyUp={noEnter} onClick={() => reply.current("same")}>{words.same}<kbd aria-hidden="true">Y</kbd></button>
        <button className="btn lg" type="button" disabled={held !== null} aria-keyshortcuts="N" onKeyDown={noEnter} onKeyUp={noEnter} onClick={() => reply.current("another")}>{words.another}<kbd aria-hidden="true">N</kbd></button>
      </div>
    </div>
  );
}

/** The count screen of a count in progress (/stock-take/:id). */
export function StockCount({ take }: { take: StockTakeView }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const location = useLocation();
  const { online, title } = useStockWrites();
  const canWrite = can(s, "inventory.stock_take.write");
  const method = (location.state as { method?: string } | null)?.method ?? "scan";
  // Read again every 10 s while the count takes entries; while it posts none come (BR-INVENTORY-031; the closing check of 25 Oct 2026, FR-5).
  const counts = useQuery({ queryKey: ["stock-take", take.id, "counts"], queryFn: () => stockTakes.counts(take.id), refetchInterval: take.status === "Open" ? 10_000 : false });
  const entries = counts.data?.data.items ?? [];
  const [device, setDevice] = useState(readDeviceName);
  const [naming, setNaming] = useState(false);
  const [scan, setScan] = useState("");
  // The latest answers, newest first: the latest is read out; the ones before it stay readable, so an answer to a scan sent while
  // others waited (an unknown code among them) is not lost under the next one.
  const [answers, setAnswers] = useState<(Answer & { id: number })[]>([]);
  const answerSeq = useRef(0);
  const setAnswer = (a: Answer) => setAnswers((xs) => [{ ...a, id: ++answerSeq.current }, ...xs].slice(0, 4));
  // Add's own send, and the scans being sent (`drain`), each with its flag: Add is not offered while either runs, so an Add answered
  // while the scans are sent never offers Add under a scan's question, whose answer an Add's question would take (UC-INVENTORY-008
  // A11 step 5; the verification of 30 Oct 2026, C12V-1; TC-INVENTORY-253).
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [qty, setQty] = useState("");
  const [supplierSerial, setSupplierSerial] = useState("");
  const [tag, setTag] = useState("");
  const [qtyErr, setQtyErr] = useState<Said<string> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);
  const pickerBox = useRef<HTMLDivElement>(null);
  const posting = take.status === "Posting";
  const writable = canWrite && online && !posting;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["stock-take", take.id] }); };
  // The question asked before a kept code or entry is sent again (A11 step 3), while it is open: the scan box, or Quantity, never takes
  // the focus from it, so the person's Y or N reaches it (step 4) — an answer's or a scan's focusing of the box comes a moment later.
  const answering = useRef<{ resolve: (r: Reply) => void; kind: Question["kind"] } | null>(null);
  const focusScan = () => { setTimeout(() => { if (!answering.current) scanRef.current?.focus(); }, 0); };
  useEffect(() => { if (method === "quantity") pickerBox.current?.querySelector("input")?.focus(); else scanRef.current?.focus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // A scanner sends its next code while the last is still being answered: each scan joins a queue and is sent in its turn, the box
  // emptied at once — none is dropped, none runs into the next (the review of 18 Oct 2026, D1-1; TC-INVENTORY-140).
  const queue = useRef<string[]>([]);
  const draining = useRef(false);
  // A scan whose answer never came (the connection dropped, or it is still being written) is sent again as it was sent, under its own
  // key, when the same code is scanned again and the counter answers that it is the same box — whatever was scanned meanwhile — so a
  // re-scan of it counts once; another box, any other scan, or a further box of the same product after an answer, is a new count (D1-12;
  // UC-INVENTORY-008 A11; TC-INVENTORY-142, -238, -250; DVN's answers of 28 Oct 2026, change 12). Kept by
  // the person and the count, so the count left and opened again keeps it (TC-INVENTORY-239), and another person or another count never
  // sends it (TC-INVENTORY-243); what it keeps is named above the scan box until it is sent again (KeptToSendAgain) — and sent again
  // only while the Worker still holds its key, 23 hours from its first send (`pastHold`; TC-INVENTORY-246).
  const lost = useMemo(() => lostAnswersOf(qc, `${s?.user.id ?? ""}:${take.id}`), [qc, s?.user.id, take.id]);
  const [keptSeq, setKeptSeq] = useState(0);
  const keptChanged = () => setKeptSeq((n) => n + 1);
  // A kept key reaching the Worker's hold changes its words — above the scan box, in its answers and under Quantity (V8I-2): the screen is
  // drawn again then though nothing else happens — the count left open overnight — and at least every hour while a key waits, so a
  // device's clock moved meanwhile is caught up with (TC-INVENTORY-246).
  useEffect(() => {
    const now = Date.now();
    const next = Math.min(...[...lost.scans.values(), ...lost.adds.values()].map((k) => k.firstSentAt + KEPT_KEY_MS).filter((at) => at > now));
    if (!Number.isFinite(next)) return;
    const t = setTimeout(() => setKeptSeq((n) => n + 1), Math.min(next - now, 3_600_000));
    return () => clearTimeout(t);
  }, [lost, keptSeq]);
  const deviceRef = useRef(device);
  deviceRef.current = device;
  const scanState = useRef({ canWrite, posting, suspended: false });
  scanState.current = { canWrite, posting, suspended: s?.organisation.status === "Suspended" };

  // The question asked before a kept code or entry is sent again (UC-INVENTORY-008 A11 step 3; DVN's answers of 28 Oct 2026, change
  // 12), one at a time. While it is open the scans after it wait (`drain`, step 5); the count left with it open answers it "left", which
  // sends nothing of it, and every question after that is "left" too (step 8).
  const [asking, setAsking] = useState<(Question & { seq: number }) | null>(null);
  const askSeq = useRef(0);
  const question = useRef<Promise<Reply> | null>(null);
  const mounted = useRef(false);
  const settle = (r: Reply) => {
    const a = answering.current;
    if (!a) return;
    answering.current = null;
    question.current = null;
    if (mounted.current) {
      setAsking(null);
      // The focus back where the counter goes on: the scan box after a scan's question, Quantity after Add's.
      if (a.kind === "scan") focusScan(); else setTimeout(() => { if (!answering.current) document.getElementById("count-qty")?.focus(); }, 0);
    }
    a.resolve(r);
  };
  const ask = (q: Question): Promise<Reply> => {
    if (!mounted.current) return Promise.resolve("left");
    const p = new Promise<Reply>((resolve) => { answering.current = { resolve, kind: q.kind }; });
    question.current = p;
    // Each question drawn afresh, so it takes the focus though one was answered a moment before (A11 step 5).
    setAsking({ ...q, seq: ++askSeq.current });
    return p;
  };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; settle("left"); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** One code sent and answered: "unknown" when nothing matches it, so the product search may take the focus. */
  const countOne = async (c: string): Promise<"done" | "unknown"> => {
    const ofCode = (k: KeptScan) => k.code === c;
    // A lost scan of this code the Worker still holds: asked first whether this is the same box (A11 step 3) — unless the device is
    // offline, where nothing would be sent: the scan is refused below, as step 1 says, its key kept (step 6). Past the hold nothing is
    // asked, and the code scanned again is a new count (V7IR-4; step 7).
    let kept: KeptScan | undefined = heldOf(lost.scans, ofCode, Date.now())[0];
    if (kept && isOnline()) {
      const reply = await ask({ kind: "scan", name: <span className="mono">{c}</span>, kept: heldOf(lost.scans, ofCode, Date.now()).length, quantity: 1 });
      // The count left with the question open: nothing of it is sent, its key kept (step 8).
      if (reply === "left") return "done";
      if (reply === "another") kept = undefined;
      else {
        // Read by the clock when it is given: the question left open past the hold sends nothing — the Worker may no longer hold its key.
        kept = heldOf(lost.scans, ofCode, Date.now())[0];
        if (!kept) {
          setAnswer({ tone: "warn", text: <><span className="mono">{c}</span> was not sent again: its answer did not arrive a day ago or more, so it may have been counted. Check its line below before you scan it again.</> });
          return "done";
        }
      }
    }
    const now = Date.now();
    const dev = deviceRef.current;
    const body: CountBody = kept?.body ?? { code: c, ...(dev ? { device: dev } : {}) };
    const key = kept?.key ?? newIdempotencyKey();
    // Answered, whatever the answer: a re-send spends its key; a new count — another box's included — leaves the code's sends past the
    // hold, which it was told to check, and never one the Worker still holds (step 2).
    const spent = () => { if (kept ? lost.scans.delete(kept.key) : dropPast(lost.scans, ofCode, Date.now())) keptChanged(); };
    try {
      const r = await stockTakes.count(take.id, body, key);
      spent();
      const line = r.data.line;
      setAnswer({ tone: r.data.alreadyCounted ? "info" : line && !line.posts && line.result === "Found" ? "warn" : "ok", text: r.data.message });
      return "done";
    } catch (err) {
      const f = isApiError(err) ? err.field("code") : undefined;
      if ((isNetworkError(err) && !err.offline) || (isApiError(err) && err.code === "REQUEST_IN_FLIGHT")) {
        // Kept from its first send: a re-send's answer lost again leaves the key's age as it was (TC-INVENTORY-246). A new count lost —
        // another box's too — is kept under its own key, beside any other of its code the Worker still holds (TC-INVENTORY-254). Its words
        // say "counted once" only until the Worker's hold passes (V8I-2).
        const firstSentAt = kept?.firstSentAt ?? now;
        if (!kept) { dropPast(lost.scans, ofCode, now); lost.scans.set(key, { key, body, firstSentAt, code: c }); }
        keptChanged();
        setAnswer({ tone: "warn", text: <><span className="mono">{c}</span> may not have been counted: its answer did not arrive. Scan it again — it is counted once.</>, once: { firstSentAt, past: pastScanAnswer(c) } });
        return "done";
      }
      if (isNetworkError(err) && err.offline) {
        // Refused before it was sent, the device offline — a scan queued behind a lost one (A9) whose turn came after the Wi-Fi dropped:
        // nothing was sent, so nothing changes, and a lost answer's key stays for its code. The re-scan of that code says it may have been
        // counted already, never "Nothing was sent" (UC-INVENTORY-008 A11; the verification of the closing check's fixes, 25 Oct 2026;
        // TC-INVENTORY-235); any other code says it was not sent, naming it, as every refusal of a scan does (the verification of round
        // 6's fixes, 25 Oct 2026, M2) — a code past the Worker's hold too, which would have gone as a new count: its line stays named.
        setAnswer(kept
          ? { tone: "warn", text: <><span className="mono">{c}</span> may not have been counted: you are offline, so it was not sent again. Scan it again once you are back online — it is counted once.</>, once: { firstSentAt: kept.firstSentAt, past: pastScanAnswer(c) } }
          : { tone: "crit", text: <><span className="mono">{c}</span> was not sent: you are offline. Scan it again once you are back online.</> });
        return "done";
      }
      // Answered: its key is spent. The re-scan of a lost answer, answered as carried out: the code was counted once, its answer not kept
      // (UC-INVENTORY-008 A8). Every refusal names its code — a fault, a refusal of the person's permission or for too many requests too
      // — so of scans answered so each says which box to scan again; only the count's own words, closed or posting, name none (A9 step
      // 2; the verification of round 7's fixes, 25 Oct 2026, V7IS-2; TC-INVENTORY-140).
      spent();
      if (carriedOut(err)) setAnswer({ tone: "ok", text: <><span className="mono">{c}</span> was counted: its answer was not kept.</> });
      else if (f?.code === "unknown") { setAnswer({ tone: "crit", text: <>Nothing matches <span className="mono">{c}</span> — search for the product</> }); return "unknown"; }
      else if (isApiError(err) && err.code === "IMMUTABLE_RECORD") setAnswer({ tone: "crit", text: err.meta.reason === "posting" ? `${take.number} is being posted: it takes no entries meanwhile.` : "This count is closed: it takes no more entries." });
      else setAnswer({ tone: "crit", text: <><span className="mono">{c}</span> — {f ? f.message : describeError(err)}</> });
      return "done";
    }
  };
  const drain = async () => {
    if (draining.current) return;
    draining.current = true;
    setSending(true);
    let last: "done" | "unknown" = "done";
    try {
      while (queue.current.length > 0) {
        // Add's question, asked beside the queue, holds the scans after it until it is answered, as a scan's does (A11 step 5).
        while (question.current) await question.current;
        last = await countOne(queue.current.shift()!);
      }
    } finally {
      draining.current = false;
      setSending(false);
      refresh();
      // A code nothing matches sends the counter to the product search — unless more scans came meanwhile, which the box keeps.
      if (last === "unknown") setTimeout(() => pickerBox.current?.querySelector("input")?.focus(), 0); else focusScan();
    }
  };
  // A scan the screen does not count says so, and sends nothing (UC-INVENTORY-008 A11 step 1, E1 step 2; the closing check of the second fix
  // round of 30 Oct 2026, C2V2-7: until then a scan was dropped unsaid whenever the count took no entries, so offline a scanner's code caught
  // off the disabled box was lost, never sent once back online). Offline it joins the queue and is refused before it is sent in its turn, as
  // any scan is, a kept code's key unchanged; while the count posts, the count's own words, naming no code; while the organisation is
  // suspended — its Owner reading — refused here before it is sent, naming its code, a kept code's in the kept-key words, its key unchanged.
  const countCode = (code: string) => {
    const c = code.trim();
    setScan("");
    const now = scanState.current;
    if (!c || !now.canWrite) return;
    if (now.posting) { setAnswer({ tone: "crit", text: `${take.number} is being posted: it takes no entries meanwhile.` }); return; }
    if (now.suspended) {
      const kept = heldOf(lost.scans, (k) => k.code === c, Date.now())[0];
      setAnswer(kept
        ? { tone: "warn", text: <><span className="mono">{c}</span> may not have been counted: the organisation is suspended, so it was not sent again. Scan it again once the suspension is lifted — it is counted once.</>, once: { firstSentAt: kept.firstSentAt, past: pastScanAnswer(c) } }
        : { tone: "crit", text: <><span className="mono">{c}</span> was not sent: the organisation is suspended. Scan it again once the suspension is lifted.</> });
      return;
    }
    queue.current.push(c);
    void drain();
  };
  const countRef = useRef(countCode);
  countRef.current = countCode;

  // A scan that lands while the focus is off the scan box — on a line's entries, on Change, on nothing — is counted all the same,
  // and its Enter presses no button; the box takes the focus back (D1-2; TC-INVENTORY-141). A person's keys there act as ever. While
  // the screen asks whether a code is the same box, the focus stays on the question, so the person's Y or N answers it (A11 step 4).
  useEffect(() => {
    const collect = scanCollector();
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const code = collect.take(e);
      if (code === null) return;
      e.preventDefault();
      e.stopPropagation();
      countRef.current(code);
      focusScan();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countQuantity = async () => {
    // Enter in Quantity too: nothing while the scans are being sent, a scan's question among them (A11 step 5).
    if (!picked || busy || draining.current || !writable) return;
    const n = Number(qty.trim());
    if (!/^\d+$/.test(qty.trim()) || n < 1 || n > 9999) { setQtyErr({ text: "A whole number from 1 to 9 999" }); return; }
    setBusy(true);
    setQtyErr(null);
    try {
      const body = {
        productId: picked.id, quantity: n, ...(picked.trackSerials ? { found: true } : {}), ...(device ? { device } : {}),
        ...(picked.trackSerials && supplierSerial.trim() ? { supplierSerial: supplierSerial.trim() } : {}), ...(picked.trackSerials && tag.trim() ? { tag: tag.trim() } : {}),
      };
      // As a scan's: Add pressed again after a lost answer, answered as the same units, is the same request, sent again as it was first
      // sent, so the shelf is counted once — its key kept by the entry sent, whatever was added meanwhile and through a press refused
      // before it is sent, the device offline (UC-INVENTORY-008 A11; TC-INVENTORY-235, -239, -251). The entry is what Add counts, never
      // the device's name: until the verification of round 7's fix-ups (25 Oct 2026, V7IR-1) the key was found by all Add sent, so a
      // device renamed between them sent Add under a new key, counted again at each press, and its line never cleared (TC-INVENTORY-245).
      const { device: _named, ...counts } = body;
      const what = JSON.stringify(counts);
      const ofEntry = (k: KeptAdd) => k.what === what;
      const entry = `${n} × ${picked.displayName}${body.supplierSerial ? ` · ${body.supplierSerial}` : ""}`;
      // Its re-send while the Worker holds the key, once the counter answers that these are the same units (A11 step 3) — asked nothing
      // offline (step 6); past the hold, the entry added again is a new count, asked nothing (V7IR-4; TC-INVENTORY-246).
      let kept: KeptAdd | undefined = heldOf(lost.adds, ofEntry, Date.now())[0];
      if (kept && isOnline()) {
        const reply = await ask({ kind: "add", name: entry, kept: heldOf(lost.adds, ofEntry, Date.now()).length, quantity: n });
        if (reply === "left") return;
        if (reply === "another") kept = undefined;
        else {
          // Read by the clock when it is given, as a scan's (step 7).
          kept = heldOf(lost.adds, ofEntry, Date.now())[0];
          if (!kept) { setQtyErr({ text: "Not sent again: the answer did not arrive a day ago or more, so it may have been counted: check its line below before you add it again" }); return; }
        }
      }
      const now = Date.now();
      const sent = kept?.body ?? body;
      const key = kept?.key ?? newIdempotencyKey();
      // Answered: a re-send spends its key; a new count — other units' included — leaves the entry's sends past the hold (step 2).
      const spent = () => { if (kept ? lost.adds.delete(kept.key) : dropPast(lost.adds, ofEntry, Date.now())) keptChanged(); };
      const counted = (message: string) => {
        spent();
        setAnswer({ tone: "ok", text: message });
        setPicked(null); setQty(""); setSupplierSerial(""); setTag("");
        refresh();
        focusScan();
      };
      try {
        counted((await stockTakes.count(take.id, sent, key)).data.message);
      } catch (err) {
        // Add pressed again after a lost answer, answered as carried out: counted once, its answer not kept.
        if (carriedOut(err)) { counted(`${picked.displayName} · ${n} counted: its answer was not kept.`); return; }
        if ((isNetworkError(err) && !err.offline) || (isApiError(err) && err.code === "REQUEST_IN_FLIGHT")) {
          // As a scan's: kept from its first send, a new count's under its own key beside any other of its entry (TC-INVENTORY-254), its
          // words saying "counted once" only until the Worker's hold passes (V8I-2).
          const firstSentAt = kept?.firstSentAt ?? now;
          if (!kept) { dropPast(lost.adds, ofEntry, now); lost.adds.set(key, { key, body: sent, firstSentAt, what, entry }); }
          keptChanged();
          setQtyErr({ text: "The answer did not arrive: press Add again — it is counted once", once: { firstSentAt, past: PAST_ADD_FIELD } });
          return;
        }
        // Refused before it was sent, the device offline: nothing was sent, so a lost answer's key stays for its entry. Past the Worker's
        // hold, or answered as other units, it is no re-send, so it says what any Add refused offline says, its line still named (V8I-4;
        // TC-INVENTORY-246, TC-INVENTORY-255).
        if (isNetworkError(err) && err.offline) {
          if (kept) { setQtyErr({ text: "You are offline, so it was not sent again: press Add once you are back online — it is counted once", once: { firstSentAt: kept.firstSentAt, past: PAST_ADD_FIELD } }); return; }
        } else spent();
        throw err;
      }
    } catch (err) {
      const f = isApiError(err) ? err.errors[0] : undefined;
      setQtyErr({ text: f ? f.message : describeError(err) });
    } finally { setBusy(false); }
  };

  const removeEntry = async (e: StockCountEntryView) => {
    if (!writable) return;
    try {
      await stockTakes.voidCount(take.id, e.id, newIdempotencyKey());
      setAnswer({ tone: "info", text: <>Entry removed: {e.quantity} × {e.productName}{e.serialNumber ? <> <span className="mono">{e.serialNumber}</span></> : null}. It stays on record.</> });
      refresh();
    } catch (err) { setAnswer({ tone: "crit", text: describeError(err) }); }
    finally { focusScan(); }
  };

  const lines = take.lines.filter((l) => !l.toConfirm);
  const apart = take.lines.filter((l) => l.toConfirm);
  const totals = { expected: lines.reduce((n, l) => n + l.expectedQty, 0), counted: lines.reduce((n, l) => n + l.countedQty, 0) };
  if (!canWrite && !can(s, "inventory.stock_take.read")) return <NoAccessPanel page="Stock take" permission="inventory.stock_take.read" />;
  // The notice, the answers and the field read the kept keys' age as the screen is drawn (V7IR-4, V8I-2).
  const drawnAt = Date.now();
  return (
    <section className="screen" id="s-stock-count">
      <div className="content">
        <PageHead
          title={<span className="row c gap-8">Count <span className="mono">{take.number}</span><StockTakeStatusChip status={take.status} /></span>}
          sub={<>{take.branchName} · {scopeWords(take)} · started {dayRange(take.startedAt, null)} {formatTime(take.startedAt)} by {personWithId(take.startedBy)} · expected quantities frozen when the count started</>}
          actions={<>
            <Link className="btn" to="/stock-take">Back to stock take</Link>
            {canWrite && <button className="btn" type="button" disabled={!online || posting} title={title} onClick={() => setAbandoning(true)}>Abandon…</button>}
            <Link className="btn primary" to={`/stock-take/${take.id}/review`}>{take.kind === "Opening" ? "Review and cost" : "Review variance"}</Link>
          </>}
        />
        {posting && <PostingNotice take={take} />}
        <Panel title="Scan" meta={<>Device: {naming
          ? <span className="row c gap-8"><TextInput aria-label="Device name" boxClass="w-180" value={device} maxLength={64} placeholder="Front desk PC 1" onChange={(e) => setDevice(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); writeDeviceName(device); setNaming(false); focusScan(); } }} autoFocus />
              <button className="btn sm" type="button" onClick={() => { writeDeviceName(device); setNaming(false); focusScan(); }}>Save</button></span>
          : <>{device || "not named"} · <button className="linkbtn" type="button" onClick={() => setNaming(true)}>{device ? "Change" : "Name this device"}</button></>}</>}>
          <div className="stack" style={{ gap: 12 }}>
            {!posting && <KeptToSendAgain lost={lost} now={drawnAt} />}
            <div className="row c gap-8 count-scan">
              <div className="input grow">
                <Icon name="barcode" />
                <input ref={scanRef} aria-label="Scan a serial, pack barcode or SKU" placeholder="Scan a serial, pack barcode or SKU" autoComplete="off" maxLength={64} value={scan}
                  disabled={!writable} title={!online ? title : undefined}
                  onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void countCode(scan); } }} />
              </div>
              <CameraScan testId="count-camera" disabled={!writable} onCode={(code) => countCode(code)} onMiss={(words) => setAnswer({ tone: "warn", text: words })} />
            </div>
            {asking && <SameOrAnother key={asking.seq} q={asking} held={posting ? `Neither answer can be given while ${take.number} is being posted: it takes no entries meanwhile.` : null} onReply={settle} />}
            <div aria-live="polite">
              {answers[0] && <Alert tone={answers[0].tone} role="status">{saidAt(answers[0], drawnAt)}</Alert>}
              {answers.length > 1 && <div className="stack gap-4">{answers.slice(1).map((a) => <div key={a.id} className="small faint">{saidAt(a, drawnAt)}</div>)}</div>}
            </div>
            <div className="divider" />
            <div className="row count-qty" ref={pickerBox}>
              <div className="grow"><ProductPicker label="Count by quantity" value={picked} onPick={setPicked} keyTag={`count-${take.id}`}
                categories={take.scope?.categories as never} allow={(p) => p.stocked && !p.isComposite}
                help={picked?.trackSerials ? "Serialised: the units entered here are found units, each given a new serial when the count is finalised" : "Pick the product and type how many are on the shelf"} /></div>
              <Field label="Quantity" htmlFor="count-qty" error={qtyErr ? saidAt(qtyErr, drawnAt) : undefined}>
                <TextInput id="count-qty" inputMode="numeric" boxClass="w-80" value={qty} disabled={!writable} onChange={(e) => { setQty(e.target.value); setQtyErr(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void countQuantity(); } }} />
              </Field>
              {picked?.trackSerials && <Field label="Supplier serial" htmlFor="count-sserial" help="Scanned from the frame; one unit"><TextInput id="count-sserial" mono boxClass="w-180" value={supplierSerial} maxLength={64} onChange={(e) => setSupplierSerial(e.target.value)} /></Field>}
              {picked?.trackSerials && take.kind === "Opening" && <Field label="Old sticker" htmlFor="count-tag" help="Kept as its tag"><TextInput id="count-tag" mono boxClass="w-120" value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} /></Field>}
              <div className="field"><label>&nbsp;</label><button className="btn" type="button" disabled={!picked || !writable || busy || sending} title={title} onClick={() => void countQuantity()}>Add</button></div>
            </div>
          </div>
        </Panel>
        <Panel title="Lines" meta={`${lines.length} line${lines.length === 1 ? "" : "s"} · ${totals.counted} counted of ${totals.expected} expected · ${posting ? "as counted · read again once the posting ends" : "refreshed every few seconds"}`} tight>
          {lines.length === 0 ? <Empty title="Nothing counted yet.">{take.kind === "Opening" ? "An opening count expects nothing: every unit counted is a line." : "Scan the first unit."}</Empty> : (
            <div className="tbl-scroll">
              <table>
                <thead><tr><th className="sku-col">SKU</th><th>Product</th><th className="r">Expected</th><th className="r">Counted</th><th className="r">Difference</th><th>Entries</th></tr></thead>
                <tbody>{lines.map((l) => {
                  const k = `${l.productId}-${l.serialId ?? ""}`;
                  const mine = entriesOf(l, entries);
                  const live = mine.filter((e) => !e.voidedAt);
                  const diff = l.countedQty - l.expectedQty;
                  return (
                    <Fragment key={k}>
                      <tr>
                        <td className="mono nw sku-col">{l.sku}</td>
                        <td>{l.productName}<div className="small faint mono sku-sub">{l.sku}</div>{(l.serialNumber || l.note || l.movedDuringCountQty !== 0) && <div className="small faint">{[l.serialNumber, l.note, l.movedDuringCountQty !== 0 ? `${signed(l.movedDuringCountQty)} moved during the count` : null].filter(Boolean).join(" · ")}</div>}</td>
                        <td className="r num">{l.expectedQty}</td>
                        <td className="r num strong">{l.countedQty}</td>
                        <td className={`r num${diff !== 0 ? " strong" : " faint"}`}>{diff === 0 ? "—" : signed(diff)}</td>
                        <td>{mine.length === 0 ? <span className="faint">none</span> : <button className="linkbtn" type="button" aria-expanded={open === k} onClick={(ev) => { setOpen(open === k ? null : k); if (ev.detail > 0) focusScan(); }}>{live.length} entr{live.length === 1 ? "y" : "ies"}{mine.length > live.length ? ` · ${mine.length - live.length} removed` : ""}</button>}</td>
                      </tr>
                      {open === k && mine.map((e) => (
                        <tr key={e.id} className="entry">
                          <td className="sku-col"></td>
                          <td colSpan={4} className={e.voidedAt ? "faint" : undefined}>
                            {e.quantity} · {shortName(e.countedBy?.displayName)}{e.device ? ` · ${e.device}` : ""} · {formatTime(e.countedAt)}{e.found ? " · found unit" : ""}{e.supplierSerial ? <> · <span className="mono">{e.supplierSerial}</span></> : null}
                            {e.voidedAt && <> · removed by {shortName(e.voidedBy?.displayName)} at {formatTime(e.voidedAt)}</>}
                          </td>
                          <td>{!e.voidedAt && canWrite && <button className="btn sm ghost" type="button" disabled={!writable} title={title} aria-label={`Remove the entry of ${e.quantity} by ${shortName(e.countedBy?.displayName)} at ${formatTime(e.countedAt)}`} onClick={() => void removeEntry(e)}>Remove</button>}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </Panel>
        {apart.length > 0 && (
          <Panel title="Confirmed, not counted" meta="at a lab or in transit · listed against their documents" tight>
            <div className="list">{apart.map((l) => (
              <div key={`${l.productId}-${l.serialId ?? ""}`} className="item"><div className="grow"><div className="primary">{l.productName} {l.serialNumber && <span className="mono">{l.serialNumber}</span>}</div>
                <div className="sub">{l.expectedState === "AtLab" ? "At lab" : "In transit"}{l.holdingDocument?.number ? ` · ${l.holdingDocument.number}` : ""}{l.scannedHere ? " · scanned here — the document still has it" : ""}</div></div></div>
            ))}</div>
          </Panel>
        )}
        {writable && <div className="kbd-hints">{asking ? <><span><kbd>Y</kbd> {questionWords(asking).same.toLowerCase()} · <kbd>N</kbd> {questionWords(asking).another.toLowerCase()}</span>
          <span><kbd>Enter</kbd> and <kbd>Space</kbd> answer nothing · scans wait behind the question</span><span>{asking.kind === "scan" ? "The scan box" : "Quantity"} takes the focus back once it is answered</span></>
          : <><span><kbd>Enter</kbd> after a scan counts it · scans sent quickly wait their turn</span><span>The scan box takes the focus back after every answer; a scan is counted wherever the focus is</span></>}
        </div>}
        {abandoning && <AbandonModal take={take} onClose={() => { setAbandoning(false); focusScan(); }} />}
      </div>
    </section>
  );
}

/** UC-INVENTORY-009 A7: a reason, then the count is Abandoned and nothing posts; no Control PIN. */
function AbandonModal({ take, onClose }: { take: StockTakeView; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    setBusy(true);
    setError(null);
    const abandoned = () => {
      navigate(`/stock-take?take=${take.id}`, { state: { notice: `${take.number} abandoned. Nothing was posted; its products may be counted again.` } });
      void qc.invalidateQueries({ queryKey: ["stock-take"] });
      void qc.invalidateQueries({ queryKey: ["stock-takes"] });
    };
    try {
      await stockTakes.abandon(take.id, why, key);
      abandoned();
    } catch (err) {
      // Pressed again after a lost answer and answered as carried out: abandoned once, its answer not kept (UC-INVENTORY-009 A7).
      if (carriedOut(err)) abandoned();
      else setError(err);
    } finally { setBusy(false); }
  };
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  return (
    <Modal title={`Abandon ${take.number}`} meta="no Control PIN · nothing posts" onClose={() => { if (!busy) onClose(); }} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Abandoning…" : "Abandon count"}</button>
    </>}>
      {error !== null && !reasonErr && <Alert tone="crit"><b>Not abandoned.</b> {describeError(error)}</Alert>}
      <p>The count keeps its number and its entries; nothing posts, and its products may be counted again at once.</p>
      <Field label="Reason" required htmlFor="abandon-reason" error={reasonErr}>
        <TextArea id="abandon-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

// --- the review (UC-INVENTORY-009 steps 1–7; UC-INVENTORY-010 steps 4–7) ------------------------------------------------------

/** /stock-take/:id/review */
export function StockReview() {
  const s = useSessionView();
  const { id = "" } = useParams();
  const allowed = can(s, "inventory.stock_take.read");
  // Read once; while it is Posting… its head alone is read again, so its progress moves on (UC-INVENTORY-010 A4, E7; `useFollowedTake`).
  const head = useQuery({ queryKey: ["stock-take", id], queryFn: () => stockTakes.get(id), enabled: allowed });
  const t = useFollowedTake(head.data?.data);
  const today = localDate(new Date());
  const [cutOver, setCutOver] = useState<string | null>(null);
  const date = cutOver ?? t?.cutOverDate ?? today;
  const opening = t?.kind === "Opening";
  // The figures stay on screen while a new cut-over date's costs are read, and with them what was typed.
  const variance = useQuery({ queryKey: ["stock-take", id, "variance", opening ? date : ""], queryFn: () => stockTakes.variance(id, opening ? date : undefined), enabled: allowed && !!t, placeholderData: keepPreviousData });
  if (!allowed) return <section className="screen" id="s-stock-review"><div className="content"><PageHead title="Review" /><NoAccessPanel page="Stock take" permission="inventory.stock_take.read" /></div></section>;
  return (
    <section className="screen" id="s-stock-review">
      <div className="content">
        <PageHead
          title={t ? <span className="row c gap-8">Review <span className="mono">{t.number}</span><StockTakeStatusChip status={t.status} /></span> : "Review"}
          sub={t ? <>{t.branchName} · {scopeWords(t)} · counted by {personWithId(t.startedBy)} · expected is the frozen figure plus what moved since the count started</> : undefined}
          actions={t ? <>{t.status === "Open" && <Link className="btn" to={`/stock-take/${t.id}`}>Back to counting</Link>}<Link className="btn" to="/stock-take">Stock take</Link></> : undefined}
        />
        {(head.isPending || variance.isPending) && <Loading what="the review" />}
        {head.isError && <ErrorAlert error={head.error} />}
        {variance.isError && <ErrorAlert error={variance.error} />}
        {t && variance.data && (opening
          ? <OpeningReview take={t} v={variance.data.data} date={date} onDate={setCutOver} onRefetch={() => void variance.refetch()} />
          : <VarianceReview take={t} v={variance.data.data} onRefetch={() => void variance.refetch()} />)}
      </div>
    </section>
  );
}

function VarianceTable({ v }: { v: VarianceView }) {
  const items = v.items.filter((l) => !l.toConfirm);
  return (
    <div className="tbl-scroll">
      <table>
        <thead><tr><th>SKU</th><th>Product</th><th className="r">Expected</th><th className="r">Counted</th><th className="r">Variance qty</th><th className="r">Variance value</th></tr></thead>
        <tbody>{items.map((l) => {
          const chips: string[] = [];
          if (l.varianceQty < 0) chips.push(`${-l.varianceQty} not found`);
          if (l.varianceQty > 0) chips.push(`${l.varianceQty} found`);
          const moved = l.movedDuringCount.map((m) => `${Math.abs(m.quantity)} ${m.type === "Receipt" ? "received" : m.type === "TransferIn" ? "transferred in" : m.type === "TransferOut" ? (m.quantity > 0 ? "back from a cancelled transfer" : "transferred out") : m.type === "Sale" ? "sold" : "moved"} during the count`);
          return (
            <tr key={`${l.productId}-${l.serialId ?? ""}`} className={l.varianceQty !== 0 ? "var" : undefined}>
              <td className="mono nw">{l.sku}</td>
              <td><div className="row c gap-8"><span>{l.productName}</span>{chips.map((c) => <span key={c} className="chip warn">{c}</span>)}</div>
                {(l.serialNumber || moved.length > 0 || l.note || (l.varianceQty !== 0 && l.unitCostCents !== null)) && <div className="small faint">{[l.serialNumber, ...moved, l.note, l.varianceQty !== 0 && l.unitCostCents !== null ? `cost ${formatMoney(l.unitCostCents)}` : null].filter(Boolean).join(" · ")}</div>}</td>
              <td className="r num">{l.expectedQty}</td>
              <td className={`r num${l.varianceQty !== 0 ? " strong" : ""}`}>{l.countedQty}</td>
              <td className={`r${l.varianceQty !== 0 ? " num strong" : " faint"}`}>{l.varianceQty !== 0 ? signed(l.varianceQty) : "—"}</td>
              <td className={`money${l.varianceQty !== 0 ? " strong" : " faint"}`}>{l.varianceQty !== 0 ? formatMoney(l.varianceValueCents) : "—"}</td>
            </tr>
          );
        })}</tbody>
        <tfoot><tr><td colSpan={2}>{v.lines} line{v.lines === 1 ? "" : "s"} · {v.varianceLines} variance line{v.varianceLines === 1 ? "" : "s"}</td><td className="r num">{v.expected}</td><td className="r num">{v.counted}</td><td className="r num">{signed(v.varianceQty)}</td><td className="money">{formatMoney(v.varianceValueCents)}</td></tr></tfoot>
      </table>
    </div>
  );
}

function ApartNote({ v, take }: { v: VarianceView; take: StockTakeView }) {
  const apart = v.items.filter((l) => l.toConfirm);
  const contactLenses = take.scope?.categories?.length === 1 && take.scope.categories[0] === "ContactLens";
  if (apart.length === 0) return <Alert tone="info">No items at lab in this count{contactLenses ? " — contact lenses are never sent to a lab" : ""}.</Alert>;
  return <Alert tone="info"><b>Confirmed, not counted:</b> {apart.map((l) => `${l.serialNumber ?? l.sku} ${l.expectedState === "AtLab" ? "at lab" : "in transit"}${l.holdingDocument?.number ? ` on ${l.holdingDocument.number}` : ""}`).join(", ")} — none posts a movement or a variance.</Alert>;
}

function VarianceReview({ take, v, onRefetch }: { take: StockTakeView; v: VarianceView; onRefetch: () => void }) {
  const s = useSessionView();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const withStepUp = useStepUp();
  const { online, title } = useStockWrites();
  const [reason, setReason] = useState("");
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const differs = v.varianceLines > 0 || v.varianceValueCents !== 0;
  // The variance as reviewed — its value and its signed units, so a change of units at an unchanged value is refused too (E8;
  // the review of 18 Oct 2026, TC-INVENTORY-154).
  const body = { reason: reason.trim() || undefined, reviewedVarianceCents: v.varianceValueCents, reviewedVarianceQty: v.varianceQty };
  const key = useMemo(() => newIdempotencyKey(), [JSON.stringify(body)]); // eslint-disable-line react-hooks/exhaustive-deps
  const finalised = () => {
    navigate(`/stock-take?take=${take.id}`, { state: { notice: `${take.number} finalised.` } });
    void qc.invalidateQueries({ queryKey: ["stock-take"] });
    void qc.invalidateQueries({ queryKey: ["stock-takes"] });
    void qc.invalidateQueries({ queryKey: ["stock"] });
  };
  const finalise = async () => {
    if (busy) return;
    if (differs && !reason.trim()) { setReasonErr("A reason is required when the count differs"); return; }
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      const r = await withStepUp((g) => stockTakes.finalise(take.id, body, { ...g, idempotencyKey: key }), {
        title: `Finalise ${take.number}`, reason: "none",
        description: `${v.lines} line${v.lines === 1 ? "" : "s"} · ${v.varianceLines} variance line${v.varianceLines === 1 ? "" : "s"} · ${formatMoney(v.varianceValueCents)} at ${take.branchName}'s weighted average. Each variance posts as a stock movement; the Control Log records the authoriser and the reason.`,
      });
      if (r) finalised();
    } catch (err) {
      // Pressed again after a lost answer — the same key, no second PIN — and answered as carried out: finalised once (A9 step 2).
      if (carriedOut(err)) finalised();
      else if (isApiError(err) && err.code === "VERSION_CONFLICT") { setStale(true); onRefetch(); }
      else setError(err);
    } finally { setBusy(false); }
  };
  const reserved = isApiError(error) && error.code === "ILLEGAL_TRANSITION" && error.meta.reason === "reserved_unit_missing";
  const apiReason = isApiError(error) ? error.field("reason")?.message : undefined;
  // A refusal of the count itself — a counted product changed since it was counted (E11), a finalisation too large (E10) — in the
  // server's words, never with its field's name (the closing check of 25 Oct 2026; TC-INVENTORY-236).
  const ofCount = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors.filter((f) => f.path !== "reason").map((f) => f.message).join(" ") : "";
  return (
    <>
      {stale && <Alert tone="warn" role="alert"><b>The variance changed since you reviewed it — check it again.</b> Stock moved while you were reviewing; the figures below are as they are now.</Alert>}
      {error !== null && !apiReason && <Alert tone="crit"><b>Not finalised.</b> {reserved ? "A missing unit is reserved for an order: release it from the order, or count it, before finalising." : ofCount || describeError(error)}</Alert>}
      <Panel title={<>Variance <span className="mono">{take.number}</span></>} meta={`${v.lines} line${v.lines === 1 ? "" : "s"} · ${v.varianceLines} variance line${v.varianceLines === 1 ? "" : "s"} · ${formatMoney(v.varianceValueCents)}`} tight>
        <VarianceTable v={v} />
        <div className="panel-body"><ApartNote v={v} take={take} /></div>
      </Panel>
      {can(s, "inventory.stock_take.finalise") && take.status === "Open" && (
        <Panel title="Finalise" meta={<LockMark>requires Control PIN</LockMark>}>
          <form className="stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); void finalise(); }}>
            <Field label="Reason" required={differs} htmlFor="finalise-reason" error={reasonErr ?? apiReason} help={differs ? "Written to the Control Log with the authoriser" : "Optional: nothing differs, so nothing posts — the Control PIN is still asked for"}>
              <TextArea id="finalise-reason" rows={2} maxLength={500} value={reason} onChange={(e) => { setReason(e.target.value); setReasonErr(null); }} />
            </Field>
            <div className="row c gap-8">
              <button className="btn primary" type="submit" disabled={busy || !online} title={title}>{busy ? "Finalising…" : "Finalise"}<LockMark /></button>
              {can(s, "inventory.stock_take.write") && <button className="btn" type="button" disabled={busy || !online} title={title} onClick={() => setAbandoning(true)}>Abandon…</button>}
              <span className="help">One Counted movement per variance line at the weighted average · stock is valued in Vision X — nothing posts to Zoho Books.</span>
            </div>
          </form>
        </Panel>
      )}
      {abandoning && <AbandonModal take={take} onClose={() => setAbandoning(false)} />}
    </>
  );
}

/**
 * How often a count Posting in parts is read again (UC-INVENTORY-010 A4, E7): every 3 seconds while its job runs, so its progress
 * moves on; every 30 once its job has stopped, which nothing but a post or an end moves on; not at all otherwise.
 */
export const postingPoll = (t: Pick<StockTakeView, "status" | "posting"> | undefined): number | false =>
  t?.status !== "Posting" ? false : t.posting && !t.posting.running ? 30_000 : 3_000;

/** A count as a screen follows it while it posts: `finishing` once its head is no longer listed and its own read has not answered yet. */
export type FollowedTake = StockTakeView & { finishing?: true };

/**
 * What a poll of a posting count's head found: its head, or none — the posting has ended — with the last head kept (`finishing`) and
 * when it was first found ended (`since`).
 */
interface Followed { head: StockTakeSummary | null; finishing: boolean; since: number | null }

/**
 * A count as a screen follows it while it posts in parts (UC-INVENTORY-010 A4, E7; the closing check of 25 Oct 2026, FR-5): its head
 * alone — its progress and its job — is read again, as often as `postingPoll` says, never its lines, which do not change while it posts:
 * every line, some 540 bytes, read again every 3 seconds made a poll of a 20 000-line opening some 10 MB. Once the head is no longer
 * listed the posting has ended, and the count is read again, its lines with it (and its review's costs) — started once, never started
 * again by a later poll while it is still coming; until it answers the count reads `finishing`, its last head kept, never the count as
 * first read, whose posting may have read stopped or its progress where it was then (the verification of the closing check's fixes,
 * 25 Oct 2026, V6IS-2). Answers the count as read, its head as it stands now.
 */
export function useFollowedTake(t: StockTakeView | undefined): FollowedTake | undefined {
  const qc = useQueryClient();
  const posting = t?.status === "Posting";
  const key = ["stock-take", t?.id ?? "", "head"];
  const head = useQuery({
    queryKey: key,
    queryFn: async (): Promise<Followed> => {
      const now = await stockTakes.postingHead(t!);
      if (now !== null) return { head: now, finishing: false, since: null };
      // No longer posting: the count is read again, its lines and its review's costs with it — the head itself is not — each once: a
      // read still coming is left to come, one read since is not read again, and only one that failed is tried again at the next poll.
      // Each poll started them all again, some 11.7 MB each at 20 000 lines.
      const last = qc.getQueryData<Followed>(key);
      const since = last?.since ?? Date.now();
      void qc.invalidateQueries({
        queryKey: ["stock-take", t!.id],
        predicate: (q) => q.queryKey[2] !== "head" && q.state.fetchStatus === "idle" && q.state.dataUpdatedAt < since,
      }, { cancelRefetch: false });
      return { head: last?.head ?? null, finishing: true, since };
    },
    enabled: posting,
    // While the count as read posts, its head is read again as its job stands, the head's own once there is one.
    refetchInterval: (query) => postingPoll(query.state.data?.head ?? t),
  });
  if (!t || !posting || !head.data) return t;
  const merged: StockTakeView = head.data.head ? { ...t, ...head.data.head, lines: t.lines } : t;
  return head.data.finishing ? { ...merged, finishing: true } : merged;
}

/** A reason as a sentence: its own full stop kept, else one added. */
const sentence = (s: string): string => (/[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`);

/**
 * UC-INVENTORY-010 A4: an opening count posting in parts — how far it has come, and that it takes no entries meanwhile; or, its job
 * stopped (E7), how far it came and why, with Post again (A7) and End posting… (A8) for a user who may post opening stock.
 */
function PostingNotice({ take }: { take: FollowedTake }) {
  const p = take.posting;
  // Its head no longer listed, its own read still coming: the posting has ended — how, the count's read says (`useFollowedTake`).
  if (take.finishing) return <Alert tone="info" role="status"><b>Posting has ended</b> — the count is being read again.</Alert>;
  if (p && !p.running) return <StoppedPosting take={take} />;
  return (
    <Alert tone="info" role="status">
      <b>Posting…{p ? ` ${formatCount(p.done)} of ${formatCount(p.total)}` : ""}</b>{p ? ` line${p.total === 1 ? "" : "s"} posted.` : ""} The count is being posted; it takes no entries meanwhile.
    </Alert>
  );
}

/**
 * A refusal of Post again at the count itself — its counted lines not yet posted whose product changed since it was counted
 * (UC-INVENTORY-010 E15 step 3, at `stockTakeId`) — as the server's words, one message after another, never with its field's name;
 * empty for any other refusal (the verification of the closing check's fixes, 25 Oct 2026, V6IW-2; TC-INVENTORY-236).
 */
const refusedAtCount = (error: unknown): string =>
  (isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors.map((f) => f.message).join(" ") : "");

/** E7, A7: the job stopped — Post again starts it under the Control PIN and a new key each time, so no stored answer is replayed. */
function StoppedPosting({ take }: { take: StockTakeView }) {
  const s = useSessionView();
  const qc = useQueryClient();
  const withStepUp = useStepUp();
  const { online, title } = useStockWrites();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ending, setEnding] = useState(false);
  const p = take.posting!;
  const may = can(s, "inventory.stock_take.finalise");
  const postAgain = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const key = newIdempotencyKey();
    try {
      const r = await withStepUp((g) => stock.postOpening({ stockTakeId: take.id, cutOverDate: take.cutOverDate ?? localDate(new Date()), costs: [] }, { ...g, idempotencyKey: key }), {
        title: `Post ${take.number} again`, reason: "none",
        description: `Starts ${take.branchName}'s opening posting again from where it stopped: the ${formatCount(p.done)} line${p.done === 1 ? "" : "s"} posted are not posted again, and the take-on serials keep their numbers.`,
      });
      if (r) void qc.invalidateQueries({ queryKey: ["stock-take", take.id] });
    } catch (err) {
      // Answered as carried out (A5): started again, its answer not kept — the count read again says how its posting stands. A guard:
      // each press goes under a new key and the PIN's refusal frees it, so the server as built never answers this so.
      if (!carriedOut(err)) setError(err);
      void qc.invalidateQueries({ queryKey: ["stock-take", take.id] });
    } finally { setBusy(false); }
  };
  return (
    <>
      <Alert tone="warn" role="alert" actions={may ? <>
        <button className="btn sm primary" type="button" disabled={busy || !online} title={title} onClick={() => void postAgain()}>{busy ? "Posting…" : "Post again"}<LockMark /></button>
        <button className="btn sm" type="button" disabled={busy || !online} title={title} onClick={() => setEnding(true)}>End posting…</button>
      </> : undefined}>
        <b>Posting stopped at {formatCount(p.done)} of {formatCount(p.total)} lines:</b> {sentence(p.stoppedReason ?? "its job is not running")}{" "}
        {may ? "Post it again to carry on — no line posts twice — or end the posting with the lines posted so far." : "Someone who may post opening stock can post it again."} The count takes no entries meanwhile.
      </Alert>
      {error !== null && <Alert tone="crit"><b>Not posted again.</b> {refusedAtCount(error) || describeError(error)}</Alert>}
      {ending && <EndPostingModal take={take} onClose={() => setEnding(false)} />}
    </>
  );
}

/** UC-INVENTORY-010 A8: a reason, then the Control PIN; the count ends Finalised with the lines posted, the others listed on it. */
function EndPostingModal({ take, onClose }: { take: StockTakeView; onClose: () => void }) {
  const oneStore = useOneStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const withStepUp = useStepUp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = take.posting!;
  const left = p.total - p.done;
  const ended = () => {
    navigate(`/stock-take?take=${take.id}`, { state: { notice: `${take.number}'s posting ended at ${formatCount(p.done)} of ${formatCount(p.total)} lines. The lines not posted are listed on the count: bring each in by an adjustment.` } });
    void qc.invalidateQueries({ queryKey: ["stock-take"] });
    void qc.invalidateQueries({ queryKey: ["stock-takes"] });
    void qc.invalidateQueries({ queryKey: ["stock"] });
  };
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => stockTakes.endPosting(take.id, why, { ...g, idempotencyKey: key }), {
        title: `End posting ${take.number}`, reason: "none",
        description: `${take.number} ends Finalised with the ${formatCount(p.done)} line${p.done === 1 ? "" : "s"} posted; the ${formatCount(left)} not posted are listed on the count. The Control Log records the authoriser, the reason and their value.`,
      });
      if (r) ended();
    } catch (err) {
      // Pressed again after a lost answer and answered as carried out: ended once, its answer not kept (A8 step 3).
      if (carriedOut(err)) ended();
      else setError(err);
    } finally { setBusy(false); }
  };
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  return (
    <Modal title={`End posting ${take.number}`} meta={<LockMark>requires Control PIN</LockMark>} onClose={() => { if (!busy) onClose(); }} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Ending…" : "End posting"}<LockMark /></button>
    </>}>
      {error !== null && !reasonErr && <Alert tone="crit"><b>Not ended.</b> {describeError(error)}</Alert>}
      <p>{take.number} ends Finalised with the {formatCount(p.done)} line{p.done === 1 ? "" : "s"} posted so far, at their costs. The {formatCount(left)} line{left === 1 ? "" : "s"} not posted {left === 1 ? "is" : "are"} listed on the count, to bring in by adjustments — dated when they post, not the cut-over. {shelfPricesWords(left, take.branchName, oneStore)} If what stopped the posting has been put right, post it again instead.</p>
      <Field label="Reason" required htmlFor="end-posting-reason" error={reasonErr}>
        <TextArea id="end-posting-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

/**
 * UC-INVENTORY-010 A8 step 3: the shelf prices the lines not posted were asked to keep (A6) — no part wrote them, and an increase
 * carries no selling price — are set again by hand, in one of three ways (the third review of DVN's answers, 25 Oct 2026; the third,
 * a product with no price at all priced on Products, named since the verification of round 3, 25 Oct 2026, R3S-4).
 */
const shelfPricesWords = (left: number, branch: string, oneStore = false): string => oneStore
  // A practice of one store: the store's price is the product's on Products unless the store charges its own, which Change <store>'s
  // price there changes — the count does not read which (UC-PRICING-002 A9 steps 4–5; the verification of 30 Oct 2026, C13-1; CHK-1).
  ? `Any shelf price ${left === 1 ? "it" : "they"} carried is not set: set it again by hand — a frame's own price on its serial once it is brought in, or, for any other product, its price on Products, or with Change ${branch}'s price there where ${branch} charges its own.`
  : `Any shelf price ${left === 1 ? "it" : "they"} carried is not set: set it again by hand — a frame's own price on its serial once it is brought in, ${branch}'s price under its Control PIN, or, for a product with no price yet, its price on Products.`;

/**
 * A line an ended posting did not post that was asked to keep a shelf price: the price, and the way that is the line's own to set it
 * again, by what its part would have written (`shelfPriceOn`, A8 step 3).
 */
export const shelfPriceNote = (l: Pick<StockTakeLineView, "shelfPriceCents" | "shelfPriceOn">, branch: string, oneStore = false): string => {
  const price = `shelf price ${formatMoney(l.shelfPriceCents)}`;
  if (l.shelfPriceOn === "Serial") return `${price} — set it again on each frame's own serial once it is brought in`;
  if (l.shelfPriceOn === "Product") return `${price} — set it again as its price on Products: it has no price yet`;
  if (l.shelfPriceOn === "Branch") return oneStore ? `${price} — set it again as its price on Products, or with Change ${branch}'s price there where ${branch} charges its own` : `${price} — set it again as ${branch}'s price under its Control PIN`;
  return `${price} — set it again by hand`;
};

/** UC-INVENTORY-010 A8: a posting ended before its last part — how far it came, that the lines not posted are listed, and their prices. */
export function EndedNotice({ take }: { take: Pick<StockTakeView, "posting" | "branchName"> }) {
  const oneStore = useOneStore();
  const p = take.posting!;
  const left = p.total - p.done;
  return (
    <Alert tone="warn" role="status">
      <b>Posting ended at {formatCount(p.done)} of {formatCount(p.total)} lines.</b> The {formatCount(left)} line{left === 1 ? "" : "s"} not posted {left === 1 ? "is" : "are"} marked "not posted": bring each in by an adjustment. {shelfPricesWords(left, take.branchName, oneStore)}
    </Alert>
  );
}

/** The SKU a `costs` error names: its message's first word — "AC-CASE-STD needs a unit cost ex VAT: …" (UC-INVENTORY-010 E2). */
const skuOf = (message: string): string => { const at = message.indexOf(" "); return at < 0 ? message : message.slice(0, at); };

/**
 * Where a refusal of the opening's post is shown (the third review, DAT-4; the verification of round 3, 25 Oct 2026, V3A-4 and
 * CHK3-verify-opening-volume-1): on the form — a counted line's cost needed, matched by its whole SKU; a typed cost out of range, by
 * its index among the costs sent; the cut-over date — or else in the alert: an error at `costs` naming no counted line (more typed
 * costs than one post takes, E14), or at the count (an opening too large). Built once for each refusal; each line then looks itself
 * up in `missing`.
 */
function refusalOf(error: unknown, costSkus: ReadonlySet<string>, sent: number): { missing: ReadonlySet<string>; placed: boolean; unplaced: string } {
  const validation = isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 ? error.errors : null;
  const missing = new Set<string>();
  if (!validation) return { missing, placed: false, unplaced: "" };
  const onForm = (e: FieldError): boolean => {
    if (e.path === "cutOverDate") return true;
    if (e.path === "costs") {
      const sku = skuOf(e.message);
      if (!costSkus.has(sku)) return false;
      missing.add(sku);
      return true;
    }
    return /^costs\.\d+\.unitCostCents$/.test(e.path) && Number(e.path.split(".")[1]) < sent;
  };
  const off = validation.filter((e) => !onForm(e));
  return { missing, placed: off.length === 0, unplaced: off.map((e) => e.message).join(" ") };
}

/**
 * UC-INVENTORY-010 steps 4–7: each counted line's unit cost (the price list's where it carries one, else its supplier price's — A1 step
 * 2, change 9), the cut-over date, the PIN.
 */
function OpeningReview({ take, v, date, onDate, onRefetch }: { take: StockTakeView; v: VarianceView; date: string; onDate: (d: string) => void; onRefetch: () => void }) {
  const oneStore = useOneStore();
  const s = useSessionView();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const withStepUp = useStepUp();
  const { online, title } = useStockWrites();
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Only an open count takes costs: posting, or posted, its costs are the posting's — shown, never typed (UC-INVENTORY-010 A4;
  // the review of 24 Oct 2026) — and a line an ended posting did not post is marked so (A8).
  const editable = take.status === "Open";
  // The lines indexed once for the review, never searched a row at a time (the verification of round 3, 25 Oct 2026, V3A-4).
  const lines = useMemo(() => new Map(v.items.map((i) => [i.productId, i])), [v.items]);
  const notPosted = useMemo(() => new Set(v.items.filter((i) => i.notPosted).map((i) => i.productId)), [v.items]);
  // A line not posted that was asked to keep a shelf price (A8 step 3): its price, to be set again by hand, and how.
  const shelfOf = (productId: string) => { const l = lines.get(productId); return l?.notPosted && l.shelfPriceCents !== null ? l : null; };
  const nameOf = (productId: string) => lines.get(productId)?.productName ?? "";
  // What is typed over a line's cost; a box cleared is untyped again, so the line takes the cost pricing filled — and shows it,
  // with its words — as the post does, which is sent nothing for it (UC-INVENTORY-010 A1 step 3; the review of change 9, 1 Nov 2026,
  // C9V-PWA-4: until then a cleared box read "Cost needed" and left the line out of the base valuation the post then counted).
  const typedOf = (productId: string): string | undefined => { const t = typed[productId]; return t !== undefined && t.trim() !== "" ? t : undefined; };
  const costOf = (c: VarianceView["costs"][number]): number | null => { const t = typedOf(c.productId); return editable && t !== undefined ? centsFromText(t) : c.unitCostCents; };
  const sent = v.costs.filter((c) => typedOf(c.productId) !== undefined && centsFromText(typedOf(c.productId)!) !== null);
  const body = { stockTakeId: take.id, cutOverDate: date, costs: sent.map((c) => ({ productId: c.productId, unitCostCents: centsFromText(typedOf(c.productId)!)! })) };
  const key = useMemo(() => newIdempotencyKey(), [JSON.stringify(body)]); // eslint-disable-line react-hooks/exhaustive-deps
  const values = v.costs.map((c) => { const u = costOf(c); return u === null || notPosted.has(c.productId) ? null : timesQuantity(cents(u), c.countedQty); });
  // Posting or posted, the base valuation is the count's own — what the posting values, or what it posted.
  const base = editable || take.baseValuationCents === null ? sumCents(values.filter((x): x is Cents => x !== null)) : take.baseValuationCents;
  const uncosted = editable ? values.filter((x) => x === null).length : 0;
  // A refusal placed once for the review, each `costs` error by its whole SKU (E2): its message begins with the line's SKU and a
  // space, an SKU holding none (BR-CATALOGUE-003), so AC-CASE-STD's error never marks AC-CASE.
  const costSkus = useMemo(() => new Set(v.costs.map((c) => c.sku)), [v.costs]);
  const refusal = useMemo(() => refusalOf(error, costSkus, sent.length), [error, costSkus, sent.length]);
  const missingFromServer = (sku: string): boolean => refusal.missing.has(sku);
  const dateErr = isApiError(error) ? error.field("cutOverDate") : undefined;
  /** Too large for one stock write, it posts in parts (`202`): the count shows how far it has come, and the page, once it is finalised, the notice (A4). */
  const posted = (status: number) => {
    const notice = `${take.number} posted as ${take.branchName}'s opening stock.`;
    if (status === 202) navigate(`/stock-take/${take.id}`, { state: { notice } });
    else navigate(`/stock-take?take=${take.id}`, { state: { notice } });
    void qc.invalidateQueries({ queryKey: ["stock-take"] });
    void qc.invalidateQueries({ queryKey: ["stock-takes"] });
    void qc.invalidateQueries({ queryKey: ["stock"] });
  };
  const post = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => stock.postOpening(body, { ...g, idempotencyKey: key }), {
        title: `Post opening stock ${take.number}`, reason: "none",
        description: `The stock-take finalisation of ${take.branchName}'s opening count: ${v.costs.length} line${v.costs.length === 1 ? "" : "s"} dated ${date}, each at its cost; every frame takes a serial dated the cut-over.`,
      });
      if (r) posted(r.status);
    } catch (err) {
      // Pressed again after a lost answer and answered as carried out (A5): posted once — at once, or in parts — its answer not kept.
      const done = carriedOut(err);
      if (done) { posted(done.answeredStatus); return; }
      setError(err);
      if (isApiError(err) && err.code === "VALIDATION_FAILED") onRefetch();
      // Read the count again: a post that failed after its start landed leaves it Posting, which the page then says (E7).
      void qc.invalidateQueries({ queryKey: ["stock-take", take.id] });
    } finally { setBusy(false); }
  };
  const { placed, unplaced } = refusal;
  return (
    <>
      {take.status === "Posting" && <PostingNotice take={take} />}
      {take.status === "Finalised" && take.posting && take.posting.done < take.posting.total && <EndedNotice take={take} />}
      {error !== null && !placed && editable && <Alert tone="crit"><b>Not posted.</b> {isApiError(error) && error.meta.reason === "branch_has_stock" ? `${take.branchName} already has stock movements: opening stock is its first stock document.` : isApiError(error) && error.meta.reason === "posting" ? `${take.number} is already being posted.` : unplaced || describeError(error)}</Alert>}
      <Panel title={<>Opening stock <span className="mono">{take.number}</span></>} meta={`${v.costs.length} counted line${v.costs.length === 1 ? "" : "s"} · ${v.counted} unit${v.counted === 1 ? "" : "s"} · costs ex VAT`} tight>
        {v.costs.length === 0 ? <Empty title="Nothing counted yet." /> : (
          <div className="tbl-scroll">
            <table>
              <thead><tr><th>SKU</th><th>Product</th><th className="r">Counted</th><th className="r">Unit cost</th><th className="r">Value</th></tr></thead>
              <tbody>{v.costs.map((c, n) => {
                const unit = costOf(c);
                if (!editable) {
                  const skipped = notPosted.has(c.productId);
                  return (
                    <tr key={c.productId}>
                      <td className="mono nw">{c.sku}</td>
                      <td>{skipped ? <div className="row c gap-8"><span>{nameOf(c.productId)}</span><span className="chip warn">not posted</span></div> : nameOf(c.productId)}
                        {skipped && shelfOf(c.productId) && <div className="small faint">{shelfPriceNote(shelfOf(c.productId)!, take.branchName, oneStore)}</div>}</td>
                      <td className="r num">{c.countedQty}</td>
                      <td className="money">{skipped || unit === null ? <span className="faint">—</span> : formatMoney(unit)}</td>
                      <td className="money">{values[n] === null ? <span className="faint">—</span> : formatMoney(values[n])}</td>
                    </tr>
                  );
                }
                const needs = unit === null || missingFromServer(c.sku);
                const i = sent.findIndex((x) => x.productId === c.productId);
                const rangeErr = i >= 0 && isApiError(error) ? error.field(`costs.${i}.unitCostCents`) : undefined;
                return (
                  <tr key={c.productId}>
                    <td className="mono nw">{c.sku}</td>
                    <td>{nameOf(c.productId)}</td>
                    <td className="r num">{c.countedQty}</td>
                    <td className="r">
                      {/* Emptied, the box greys the filled cost it falls back to, and gives it back once left (A1 step 3). */}
                      <TextInput aria-label={`Unit cost of ${c.sku}`} mono boxClass="w-120" inputMode="decimal" value={typed[c.productId] ?? textFromCents(c.unitCostCents)} placeholder={c.unitCostCents !== null ? textFromCents(c.unitCostCents) : "Cost ex VAT"} error={(needs && missingFromServer(c.sku)) || !!rangeErr}
                        onChange={(e) => { const t = e.target.value; setTyped((x) => ({ ...x, [c.productId]: t })); }}
                        onBlur={() => setTyped((x) => { if (x[c.productId] === undefined || x[c.productId]!.trim() !== "") return x; const { [c.productId]: _, ...rest } = x; return rest; })} />
                      {rangeErr ? <div className="error" role="alert">A cost from R 0.00 to R 999 999.99</div>
                        : needs ? <div className={missingFromServer(c.sku) ? "error" : "small faint"} role={missingFromServer(c.sku) ? "alert" : undefined}>Cost needed</div>
                          : c.source === "PriceList" && typedOf(c.productId) === undefined ? <div className="small faint">from the price list{c.priceListName ? ` · ${c.priceListName}` : ""}</div>
                            : c.source === "SupplierPrice" && typedOf(c.productId) === undefined ? <div className="small faint">from its supplier price</div> : null}
                    </td>
                    <td className="money">{values[n] === null ? <span className="faint">—</span> : formatMoney(values[n])}</td>
                  </tr>
                );
              })}</tbody>
              <tfoot><tr><td colSpan={4}>Base valuation{uncosted > 0 ? ` · ${uncosted} line${uncosted === 1 ? "" : "s"} still need${uncosted === 1 ? "s" : ""} a cost` : ""}{!editable && notPosted.size > 0 ? ` · ${notPosted.size} line${notPosted.size === 1 ? "" : "s"} not posted` : ""}</td><td className="money">{formatMoney(base)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </Panel>
      {can(s, "inventory.stock_take.finalise") && take.status === "Open" && (
        <Panel title="Post opening stock" meta={<LockMark>requires Control PIN</LockMark>}>
          <form className="stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); void post(); }}>
            <Field label="Cut-over date" required htmlFor="cutover-date" error={dateErr ? (dateErr.code === "future" ? "The cut-over date can't be after today" : dateErr.code === "before_count" ? "The cut-over date can't be before the count was opened" : "A date") : undefined}
              help="The opening stock movements and every frame's take-on serial are dated the cut-over">
              <TextInput id="cutover-date" type="date" boxClass="w-180" value={date} onChange={(e) => onDate(e.target.value)} />
            </Field>
            <div className="row c gap-8">
              <button className="btn primary" type="submit" disabled={busy || !online} title={title}>{busy ? "Posting…" : "Post opening stock"}<LockMark /></button>
              <span className="help">Refused once {take.branchName} has any stock movement · importing opening stock from a file comes in Sprint 11.</span>
            </div>
          </form>
        </Panel>
      )}
    </>
  );
}
